import {
  CalendarDays,
  Church,
  Mail,
  Send,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import type { PlatformInvitation } from "@/api/platformInvitations";
import { InvitationActions } from "@/components/platform/invitations/InvitationActions";
import { InvitationActivity} from "@/components/platform/audit/InvitationActivity";

interface InvitationDetailsPanelProps {
  invitation: PlatformInvitation | null;
  onInvitationReplaced?: (
    invitation: PlatformInvitation,
  ) => void;
}

interface DetailItemProps {
  label: string;
  value: string;
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return "Not available";
  }

  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getAdministratorName(
  invitation: PlatformInvitation,
) {
  const name = [
    invitation.invited_first_name,
    invitation.invited_last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || "Not provided";
}

function getStatusClasses(status: string) {
  switch (status.toUpperCase()) {
    case "ACCEPTED":
      return "border-green-200 bg-green-100 text-green-700";

    case "PENDING":
      return "border-amber-200 bg-amber-100 text-amber-700";

    case "EXPIRED":
      return "border-slate-200 bg-slate-100 text-slate-700";

    case "REVOKED":
      return "border-red-200 bg-red-100 text-red-700";

    default:
      return "border-slate-200 bg-slate-100 text-slate-700";
  }
}

function DetailItem({
  label,
  value,
}: DetailItemProps) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </dt>

      <dd className="mt-1 break-words text-sm font-medium text-slate-900">
        {value}
      </dd>
    </div>
  );
}

export function InvitationDetailsPanel({
  invitation,
  onInvitationReplaced,
}: InvitationDetailsPanelProps) {
  if (!invitation) {
    return (
      <div className="flex min-h-[500px] items-center justify-center p-8">
        <div className="max-w-sm text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Mail className="h-5 w-5 text-slate-500" />
          </div>

          <h2 className="mt-4 font-semibold text-slate-950">
            Select an invitation
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            Choose an invitation from the list to view its
            administrator, status, delivery, and expiration details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="border-b border-slate-200 px-6 py-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Invitation details
            </p>

            <h2 className="mt-1 text-xl font-semibold text-slate-950">
              {invitation.church_name}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {invitation.invited_email}
            </p>
          </div>

          <span
            className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusClasses(
              invitation.status,
            )}`}
          >
            {invitation.status}
          </span>
        </div>
      </div>

      <div className="space-y-6 p-6">
        <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
          <div className="mb-4 flex items-center gap-2">
            <Church className="h-4 w-4 text-slate-500" />

            <h3 className="font-semibold text-slate-950">
              Church
            </h3>
          </div>

          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailItem
              label="Church name"
              value={invitation.church_name}
            />

            <DetailItem
              label="Church slug"
              value={invitation.church_slug}
            />

            <DetailItem
              label="Church ID"
              value={invitation.church_id}
            />

            <DetailItem
              label="Invitation type"
              value={invitation.invitation_type}
            />
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 p-5">
          <div className="mb-4 flex items-center gap-2">
            <UserRound className="h-4 w-4 text-slate-500" />

            <h3 className="font-semibold text-slate-950">
              Administrator
            </h3>
          </div>

          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailItem
              label="Name"
              value={getAdministratorName(invitation)}
            />

            <DetailItem
              label="Email"
              value={invitation.invited_email}
            />

            <DetailItem
              label="Role"
              value={invitation.role_code}
            />

            <DetailItem
              label="Invitation ID"
              value={invitation.id}
            />
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 p-5">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-slate-500" />

            <h3 className="font-semibold text-slate-950">
              Invitation lifecycle
            </h3>
          </div>

          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailItem
              label="Status"
              value={invitation.status}
            />

            <DetailItem
              label="Version"
              value={String(invitation.invitation_version)}
            />

            <DetailItem
              label="Created"
              value={formatDateTime(invitation.created_at)}
            />

            <DetailItem
              label="Updated"
              value={formatDateTime(invitation.updated_at)}
            />

            <DetailItem
              label="Accepted"
              value={formatDateTime(invitation.accepted_at)}
            />

            <DetailItem
              label="Revoked"
              value={formatDateTime(invitation.revoked_at)}
            />

            <DetailItem
              label="Revocation reason"
              value={invitation.revocation_reason || "Not provided"}
            />
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 p-5">
          <div className="mb-4 flex items-center gap-2">
            <Send className="h-4 w-4 text-slate-500" />

            <h3 className="font-semibold text-slate-950">
              Delivery
            </h3>
          </div>

          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailItem
              label="Send count"
              value={String(invitation.send_count)}
            />

            <DetailItem
              label="First sent"
              value={formatDateTime(invitation.sent_at)}
            />

            <DetailItem
              label="Last sent"
              value={formatDateTime(invitation.last_sent_at)}
            />
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 p-5">
          <div className="mb-4 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-slate-500" />

            <h3 className="font-semibold text-slate-950">
              Expiration
            </h3>
          </div>

          <DetailItem
            label="Expires"
            value={formatDateTime(invitation.expires_at)}
          />
        </section>

        <section className="rounded-xl border border-slate-200 p-5">

            <h3 className="mb-5 font-semibold">
                Invitation Activity
            </h3>

            <InvitationActivity
                churchId={invitation.church_id}
            />

        </section>

        <InvitationActions
          key={invitation.id}
          invitation={invitation}
          onInvitationReplaced={
            onInvitationReplaced
          }
        />
      </div>
    </div>
  );
}

